import { createFileRoute, notFound } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { NOT_FOUND_HEAD_FALLBACK } from "@/lib/brandIcons";
import { blogPostHead } from "@/lib/blog/seo";
// The presentational layout is shared with the author draft preview (#280).
import { BlogPostArticle } from "#/components/blog/BlogPostArticle";

// See index.tsx: cache only a 200, never a notFound()/404.
const CACHE = "public, s-maxage=60, stale-while-revalidate=86400";

// Path A: read the single published post directly from Postgres on the SSR server;
// markdown is rendered to HTML server-side (in the query layer) so markdown-it never
// ships to the client. Server-only deps are imported inside the handler.
const getBlogPost = createServerFn({ method: "GET" })
  .validator((slug: string) => slug)
  .handler(async ({ data: slug }) => {
    const { loadBlogPost, resolveDogfoodWorkspaceId } = await import(
      "@/lib/blog/queries"
    );
    const workspaceId = resolveDogfoodWorkspaceId();
    return workspaceId ? await loadBlogPost(workspaceId, slug) : undefined;
  });

export const Route = createFileRoute("/blog/$slug")({
  ssr: true,
  loader: async ({ params }) => {
    const data = await getBlogPost({ data: params.slug });
    if (!data) throw notFound();
    return data;
  },
  headers: ({ loaderData }) =>
    loaderData
      ? { "cache-control": CACHE }
      : { "cache-control": "no-store" },
  // notFound()-branch icon fallback + the `as never` cast it needs: see
  // `NOT_FOUND_HEAD_FALLBACK`'s docblock (#1099 / US2).
  head: ({ loaderData }) =>
    (loaderData
      ? blogPostHead(
          loaderData.blog,
          loaderData.post,
          loaderData.canonical,
          loaderData.feedUrl,
          // Derived in the loader, where the markdown still exists (#926).
          loaderData.itemList,
          loaderData.faqPage,
        )
      : NOT_FOUND_HEAD_FALLBACK) as never,
  component: BlogPostPage,
});


function BlogPostPage() {
  const { post, htmlBody, morePosts } = Route.useLoaderData();
  return (
    <BlogPostArticle post={post} htmlBody={htmlBody} morePosts={morePosts} />
  );
}
